import type { ResolvableScript } from '@unhead/vue';
import { h, ref } from 'vue';
import { useRuntimeConfig, useNuxtApp, useHead } from '#app';
import { Cookie } from '@nuxt-common/util/app/utils/cookie';
import getVisitorId from '#shared/utils/visitorId';
import { getGecxLoaderUrl } from '#shared/utils/gecx';
import { useEnvProps } from '~/stores/envProps';
import { useAskAIAnalytics } from '~/composables/features/useAskAIAnalytics.ts';
import { useAskAIFocus } from '~/composables/accessibility/useAskAIFocus';
import useExperimentsClientIds from '~/composables/useExperimentsClientIds';
import type { PreloadLink } from '~/types/ask-ai';
import { useAskAIRefresh } from './useAskAIRefresh';

const DEEPLINK_QUERY = 'open_ask_ai';

const GECX_LAUNCH_SCRIPT = `
  (() => {
    const openAskAi = (payload) => {
      if (window.googleAgent) {
        window.googleAgent.openChat(payload);
      } else {
        window.googleAgentOpen = true;
        window.pendingOpenAskAiPayload = payload;
        window.dispatchEvent(new CustomEvent('google-agent-open'));
      }
    };

    window.openAskAi = openAskAi;

    const urlParams = new URLSearchParams(location?.search);
    if (urlParams.get('${DEEPLINK_QUERY}') === 'true') {
      window.openAskAi();
    }
  })()
`;

const GECX_LOADER_SCRIPT = `
  (function(){
    'use strict';
    function b(){this.m=this.v.bind(this);this.l=this.close.bind(this);this.A=this.u.bind(this);this.B=this.o.bind(this);["complete","interactive"].includes(document.readyState)?this.u():window.addEventListener("DOMContentLoaded",this.A);window.addEventListener("google-agent-open",this.m);window.addEventListener("google-agent-loaded",this.B)}
    b.prototype.v=function(){var a=this;window.googleAgent?this.o():this.g&&(window.googleAgentOpen=!0,this.g.style.display="block",setTimeout(function(){a.h&&(a.h.style.opacity="1");a.i&&(a.i.style.opacity="1")},25))};b.prototype.close=function(){var a=this;this.g&&(this.h&&(this.h.style.opacity="0"),this.i&&(this.i.style.opacity="0"),window.googleAgentOpen=!1,setTimeout(function(){window.googleAgentOpen=!1;a.g&&(a.g.style.display="none")},250))};
    b.prototype.u=function(){this.g=document.createElement("gsa-agent");var c=document.createElement("div");
    this.h=document.createElement("div");this.i=document.createElement("div");this.j=document.createElement("div");this.j.ariaLabel="Close";var d=document.createElement("div"),e=document.createElement("div");this.g.style.display="none";this.h.className="gsa-loader-backdrop loaded";this.i.className="gsa-loader-window loaded";this.j.className="gsa-loader-close loaded";
    d.className="gsa-loader-skeleton loaded";e.className="gsa-loader-skeleton loaded";this.i.appendChild(this.j);this.i.appendChild(d);this.i.appendChild(e);c.appendChild(this.h);c.appendChild(this.i);this.g.appendChild(c);document.body.appendChild(this.g);
    this.h.addEventListener("click",this.l);this.j.addEventListener("click",this.l);window.googleAgentOpen===!0&&this.v()};
    b.prototype.o=function(){if(this.g&&this.g.parentNode){if(this.g){this.h&&this.h.removeEventListener("click",this.l);this.j&&this.j.removeEventListener("click",this.l);var a;(a=this.g.parentNode)==null||a.removeChild(this.g)}window.removeEventListener("google-agent-open",this.m);window.removeEventListener("google-agent-loaded",this.B);window.removeEventListener("load",this.A)}window.googleAgentOpen===!0&&(window.googleAgentOpen=!1,window.googleAgent&&window.googleAgent.openChat())};new b;
  })();
`;

const TOKEN_ENDPOINT = '/search/v1/conversation/get-token/public';

interface TokenInfo {
  sessionId: string;
  token: string;
  expiry: string;
  encryptedPayload?: string;
}

export const useAskAI = () => {
  const config = useRuntimeConfig(); // not available during csr
  const { isMcom } = useEnvProps();
  const userIdCookieKey = `${isMcom ? 'macys' : 'bloomingdales'}_online_uid`;
  const { initAskAIRefreshTriggers, updatePreviousMetadata } = useAskAIRefresh();

  const scripts = ref<Array<ResolvableScript>>([]);
  const gecxScriptData: ResolvableScript = {
    async: true,
    type: 'text/javascript',
    crossorigin: 'anonymous',
  };

  const handleGoogleAgentLoaded = async (sessionId: string) => {
    const visitorId = getVisitorId();
    const userId = Cookie.get(userIdCookieKey) || '';
    const storeId = Cookie.get('CSL') || '';

    // Keep track of the IDs sent to Google to avoid redundant refreshes
    updatePreviousMetadata(userId, visitorId);

    const bcomParams: Record<string, string> = {};

    if (!isMcom) {
      bcomParams.store = 'BCOM';
    }

    let response;
    try {
      response = await $fetch<TokenInfo>(TOKEN_ENDPOINT, {
        method: 'POST',
        body: {
          sessionId,
          visitorId,
          userId,
          storeId,
          ...bcomParams,
        },
      });
    } catch (error) {
      console.error(`Error fetching GECX token for session ID: ${sessionId}`, error);
      throw error;
    }

    const { sessionId: newSessionId, token, expiry, encryptedPayload } = response || {};

    const params: Partial<TokenInfo> & { token_expiry: string } = {
      sessionId: newSessionId,
      token,
      token_expiry: expiry,
    };

    if (encryptedPayload) {
      params.encryptedPayload = encryptedPayload;
    }
    return params;
  };
  const links = ref<Array<PreloadLink>>([]);
  const id = config.public?.gecxId || (import.meta.client ? window.__NAVIGATION__GECX__ID__ : undefined);
  const gecxFeatures = config.public?.gecxFeatures || (import.meta.client ? window.__NAVIGATION__GECX__FEATURES__ : undefined);
  const loaderUrl = getGecxLoaderUrl(id, gecxFeatures);

  if (id) {
    links.value = [
      {
        rel: 'preload',
        href: loaderUrl,
        as: 'script',
        crossorigin: 'anonymous',
      },
    ];
  }

  if (import.meta.client) {
    if (!id) {
      return;
    }
    gecxScriptData.src = loaderUrl;

    const experimentIds = useExperimentsClientIds();
    const gecxSettings = {
      'hide-entry-toggle': true,
    };
    const { $initAskAIListeners } = useNuxtApp();
    $initAskAIListeners();
    useAskAIAnalytics();
    useAskAIFocus();
    scripts.value = [gecxScriptData];

    window.googleAgentSettings = {
      ...window.googleAgentSettings,
      id,
      tokenFunc: handleGoogleAgentLoaded,
      settings: gecxSettings,
      experimentIds: experimentIds.value,
    };

    initAskAIRefreshTriggers();
  }

  useHead({
    script: scripts,
    link: links,
  });

  const GecxLaunchScript = () => {
    return h('script', { 'hydrate-never': true, innerHTML: GECX_LAUNCH_SCRIPT });
  };

  const GecxScript = () => {
    return h('script', { 'hydrate-never': true, innerHTML: GECX_LOADER_SCRIPT });
  };

  return {
    GecxScript,
    GecxLaunchScript,
    GECX_LOADER_SCRIPT,
  };
};
